import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowDownRight,
  IconCash,
  IconChartBar,
  IconReceipt2,
  IconTruck,
} from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { PageContainer } from "../../../../components/common/page-container";
import { Heading, Text } from "../../../../components/common/typography";
import { StatCard } from "../../../../components/common/stat-card";
import { SectionCard } from "../../../../components/common/section-card";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { Shimmer, ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { Button } from "../../../../components/common/button";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { dollarFormat } from "../../../../utils/currencies";
import { useAdminMarginSnapshot, type MarginRow } from "../../hooks/useAdminMarginSnapshot";

function formatSignedAmount(amount: number, currency: string) {
  return dollarFormat(amount, currency);
}

function formatDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString();
}

export function AdminMarginPage() {
  const navigate = useNavigate();
  const { rows, summary, isLoading, isDetailsLoading, error } = useAdminMarginSnapshot();

  const columns = useMemo<SimpleTableColumn<MarginRow>[]>(() => [
    {
      key: "order",
      label: "Order",
      className: "flex-[2.1]",
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <Text variant="bodyBold" tone="dark">
            {row.saleName}
          </Text>
          <Text variant="bodySm" tone="muted">
            {formatDate(row.saleDate)} · {row.purchaseOrderCount} purchase orders · {row.shipmentCount} shipments
          </Text>
        </div>
      ),
    },
    {
      key: "revenue",
      label: "Revenue",
      className: "flex-1",
      render: (row) => (
        <CurrencyAmount amount={row.revenue} currency={row.currency} variant="bodyBold" />
      ),
    },
    {
      key: "supplierCost",
      label: "Supplier Cost",
      className: "flex-1",
      render: (row) => (
        <CurrencyAmount amount={row.supplierCost} currency={row.currency} variant="bodyBold" />
      ),
    },
    {
      key: "estimatedDelivery",
      label: "Delivery Est.",
      className: "flex-1",
      render: (row) => (
        <CurrencyAmount amount={row.estimatedDelivery} currency={row.currency} variant="bodyBold" />
      ),
    },
    {
      key: "actualDelivery",
      label: "Delivery Actual",
      className: "flex-1",
      render: (row) => (
        <CurrencyAmount amount={row.actualDelivery} currency={row.currency} variant="bodyBold" />
      ),
    },
    {
      key: "variance",
      label: "Variance",
      className: "flex-1",
      render: (row) => (
        <Text
          variant="bodyBold"
          tone="body"
          className={row.deliveryVariance > 0 ? "text-accentError" : "text-accentGreenDark"}
        >
          {formatSignedAmount(row.deliveryVariance, row.currency)}
        </Text>
      ),
    },
    {
      key: "margin",
      label: "Recorded Margin",
      className: "flex-1",
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <Text
            variant="bodyBold"
            tone="body"
            className={row.recordedMargin >= 0 ? "text-accentGreenDark" : "text-accentError"}
          >
            {formatSignedAmount(row.recordedMargin, row.currency)}
          </Text>
          <Text variant="bodyXs" tone="muted">
            Est. {formatSignedAmount(row.estimatedMargin, row.currency)}
          </Text>
        </div>
      ),
    },
  ], []);

  return (
    <PageContainer>
      <div className="flex w-full flex-col gap-6 px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
        <div className="flex flex-col gap-2">
          <Heading variant="h2" tone="dark">
            Margin Dashboard
          </Heading>
          <Text variant="bodyLg" tone="muted">
            Confirmed sales with linked purchase orders. Delivery stays buyer-included; this view tracks internal cost and margin only.
          </Text>
        </div>

        <div className="grid grid-cols-1 gap-4 max-md:gap-3 xl:grid-cols-5">
          <StatCard
            icon={<IconCash size={24} />}
            label="Revenue"
            value={dollarFormat(summary.revenue, summary.currency)}
            sub={`${rows.length} orders`}
          />
          <StatCard
            icon={<IconReceipt2 size={24} />}
            label="Supplier cost"
            value={dollarFormat(summary.supplierCost, summary.currency)}
            sub={`${summary.purchaseOrderCount} purchase orders`}
          />
          <StatCard
            icon={<IconTruck size={24} />}
            label="Delivery estimate"
            value={dollarFormat(summary.estimatedDelivery, summary.currency)}
            sub={`${summary.shipmentCount} shipments`}
          />
          <StatCard
            icon={<IconArrowDownRight size={24} />}
            label="Recorded delivery"
            value={dollarFormat(summary.actualDelivery, summary.currency)}
            sub={summary.deliveryVariance !== 0 ? `Variance ${dollarFormat(summary.deliveryVariance, summary.currency)}` : "Matches estimate"}
          />
          <StatCard
            icon={<IconChartBar size={24} />}
            label="Recorded margin"
            value={dollarFormat(summary.recordedMargin, summary.currency)}
            sub={`Estimated margin ${dollarFormat(summary.estimatedMargin, summary.currency)}`}
          />
        </div>

        <SectionCard
          shadow
          className="max-md:p-4"
          title="Recent order margins"
          subtitle="The table below is the latest operational snapshot. Open the order for shipment-level internal cost detail."
          action={
            <Button
              variant="underlinedGhostDark"
              size="sm"
              onClick={() => navigate(AppRoutes.PURCHASE_ORDERS)}
            >
              Open Orders
            </Button>
          }
        >
          {isLoading || isDetailsLoading ? (
            <ShimmerLoader message="Loading margin snapshot…">
              <div className="flex flex-col gap-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Shimmer key={idx} className="h-16 w-full" />
                ))}
              </div>
            </ShimmerLoader>
          ) : error ? (
            <div className="rounded-2xl border border-borderDefault bg-bgLight px-6 py-8">
              <Text variant="bodyBold" tone="dark">
                We couldn't load the margin snapshot.
              </Text>
              <Text variant="bodySm" tone="muted">
                Please try again in a moment.
              </Text>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={IconChartBar}
              title="No margin data yet"
              description="Confirmed sales with linked purchase orders and shipment records will appear here once the order flow starts moving."
            />
          ) : (
            <SimpleTable
              columns={columns}
              data={rows}
              keyExtractor={(row) => String(row.saleId)}
              onRowClick={(row) => navigate(AppRoutes.ORDER_DETAILS.replace(":id", String(row.saleId)))}
            />
          )}
        </SectionCard>
      </div>
    </PageContainer>
  );
}
